import { useState } from "react";
import { Plus, X } from "lucide-react";
import { CODING_LANGS } from "./CodingRoundSection";

const FIELD = "w-full mt-1 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none";
const MONO = "w-full px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none font-mono text-xs";
const LABEL = "text-xs text-slate-400 uppercase tracking-wider";

export default function ProblemEditor({ problem, langs, onChange, prefix }) {
  const [lang, setLang] = useState(langs[0]);
  const activeLang = langs.includes(lang) ? lang : langs[0];
  const set = (patch) => onChange({ ...problem, ...patch });
  const examples = problem.examples || [];
  const setEx = (i, k, v) => set({ examples: examples.map((e, j) => (j === i ? { ...e, [k]: v } : e)) });

  return (
    <div className="space-y-3" data-testid={`${prefix}-problem-editor`}>
      <div>
        <label className={LABEL}>Title</label>
        <input data-testid={`${prefix}-problem-title`} value={problem.title} onChange={(e) => set({ title: e.target.value })} className={FIELD} />
      </div>
      <div>
        <label className={LABEL}>Problem statement</label>
        <textarea data-testid={`${prefix}-problem-description`} value={problem.description} onChange={(e) => set({ description: e.target.value })} rows={6} className={FIELD} />
      </div>
      <div>
        <div className="flex items-center justify-between">
          <span className={LABEL}>Examples</span>
          <button type="button" data-testid={`${prefix}-problem-add-example`} onClick={() => set({ examples: [...examples, { input: "", output: "" }] })}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-400 hover:text-indigo-300">
            <Plus className="w-3 h-3" /> Add example
          </button>
        </div>
        <div className="space-y-2 mt-1">
          {examples.map((ex, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-start" data-testid={`${prefix}-problem-example-${i}`}>
              <textarea value={ex.input} onChange={(e) => setEx(i, "input", e.target.value)} placeholder="Input" rows={2} className={MONO} />
              <textarea value={ex.output} onChange={(e) => setEx(i, "output", e.target.value)} placeholder="Expected output" rows={2} className={MONO} />
              <button type="button" onClick={() => set({ examples: examples.filter((_, j) => j !== i) })} className="p-1.5 text-slate-400 hover:text-rose-400" title="Remove example">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`${LABEL} mr-1`}>Starter code</span>
          {langs.map(l => (
            <button type="button" key={l} data-testid={`${prefix}-problem-starter-${l}`} onClick={() => setLang(l)}
              className={`px-2 py-0.5 text-[11px] rounded-md border transition-colors ${l === activeLang ? "border-indigo-500 text-indigo-400 bg-indigo-500/10" : "border-[color:var(--bd-sub)] text-slate-400"}`}>
              {CODING_LANGS.find(c => c.key === l)?.label || l}
            </button>
          ))}
        </div>
        <textarea data-testid={`${prefix}-problem-starter-code`} value={problem.starter?.[activeLang] || ""} rows={6} spellCheck={false}
          onChange={(e) => set({ starter: { ...(problem.starter || {}), [activeLang]: e.target.value } })} className={`${MONO} mt-1.5`} />
      </div>
    </div>
  );
}
